/**
 * 채팅 "URL만 있는 메시지" → 사이트 미리보기(도메인 칩) 변환용 순수 util.
 * (chat-link-preview — 2026-06-30, 프론트 단독·additive)
 *
 * 정책:
 *   - 메시지 본문이 단일 안전 http(s) URL 하나뿐일 때만 미리보기 카드로 전환하고 raw URL
 *     문자열은 숨긴다("URL만 있는 메시지" 범위). 본문에 URL 외 텍스트가 섞이면 미적용.
 *   - 프론트 단독이라 CORS 로 임의 사이트 OG(제목·이미지)는 가져올 수 없어 도메인+파비콘
 *     수준의 경량 미리보기를 렌더한다. 파비콘 로드 실패(폐쇄망·favicon 부재)는 컴포넌트가
 *     일반 아이콘으로 폴백 → 어떤 경우에도 raw URL 노출 0.
 *   - http/https 스킴만 허용 (javascript:/data:/file: 등 차단 — XSS 회피).
 *
 * additive — 기존 렌더 경로(renderMessageMarkdown) 무변경. soleUrl 이 아닌 메시지는 영향 0.
 */

export interface LinkChip {
  /** 안전 검증된 클릭 대상 (http/https). */
  href: string;
  /** 도메인 (hostname[:port]) — 칩 제목. */
  host: string;
  /** 도메인 이하 경로+쿼리 (있으면 보조 라인). 루트면 ''. */
  path: string;
  /** 파비콘 후보 URL (origin/favicon.ico). 로드 실패는 컴포넌트가 폴백 처리. */
  faviconUrl: string;
}

/**
 * 메시지 본문이 "단일 안전 http(s) URL 하나뿐"이면 그 URL 을 반환, 아니면 null.
 * - 앞뒤 공백은 무시하되, 본문 중간에 공백/개행이 있으면(=URL 외 토큰 존재) null.
 * - http/https 스킴만.
 */
export function extractSoleUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  const t = content.trim();
  // 공백/개행이 남아 있으면 URL 외 텍스트가 섞인 것 → "URL만" 아님.
  if (!t || /\s/.test(t)) return null;
  if (!/^https?:\/\//i.test(t)) return null;
  try {
    const u = new URL(t);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (!u.hostname) return null;
    return t;
  } catch {
    return null;
  }
}

/**
 * 안전 URL → 칩 표시용 파트. extractSoleUrl 통과 url 을 가정하지만 방어적으로 재검증한다.
 * 파싱 실패·비-http 스킴이면 null (컴포넌트는 null 시 아무것도 렌더하지 않아 raw URL 누출 회피).
 */
export function parseLinkChip(url: string): LinkChip | null {
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (!u.hostname) return null;
    const rawPath = `${u.pathname}${u.search}`;
    return {
      href: url,
      host: u.host,
      path: rawPath === '/' ? '' : rawPath,
      faviconUrl: `${u.origin}/favicon.ico`,
    };
  } catch {
    return null;
  }
}
